import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MammothMascot } from "../components/MammothMascot";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Flame, Check, Heart, Wind, Anchor, BookOpen, RefreshCcw, MessageCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";

const iconMap = { heart: Heart, wind: Wind, anchor: Anchor, feather: BookOpen, "refresh-ccw": RefreshCcw, "message-circle": MessageCircle };
const taskRoute = { mood: "/mood", breathing: "/breathing", grounding: "/grounding", journal: "/journal", cbt: "/cbt", coach: "/coach" };

export default function Dashboard() {
  const [data, setData] = useState(null);
  const { user, setUser } = useAuth();
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard");
      setData(data);
      setUser(data.user);
    } catch (err) {
      console.error("dashboard load failed:", err);
    }
  }, [setUser]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="py-20 text-center opacity-60">Loading…</div>;
  const { level, next_level, progress_to_next, today_plan, cornerstones } = data;

  const doneCount = today_plan.filter(p => p.done).length;

  return (
    <div className="animate-fadeUp">
      <div className="pt-2 sm:pt-4">
        <div className="text-xs font-bold uppercase tracking-[0.25em] opacity-60">Stage {level.level} · {level.aesthetic}</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">
          Hey {user?.display_name?.split(" ")[0] || "friend"}.<br />
          <span className="italic font-medium" style={{ color: level.accent === "#FFFFFF" ? level.text : level.text }}>{level.tagline}</span>
        </h1>
      </div>

      <div className="grid md:grid-cols-12 gap-5 mt-8">
        {/* Hero Bento — mammoth + xp */}
        <div className="md:col-span-8 rounded-3xl p-7 sm:p-9 shadow-lg border border-[#0e203b]/5 relative overflow-hidden" style={{ backgroundColor: level.surface }} data-testid="dashboard-hero">
          {/* mascot pushed to background — non-interactive, behind all content.
              Rendered as a raw image (instead of MammothMascot) so the chip's
              cyan band can bleed to the card's top/bottom edges with no gap. */}
          <img
            src="/mammoth-light.png"
            alt=""
            aria-hidden="true"
            draggable={false}
            className="pointer-events-none absolute select-none"
            style={{
              zIndex: 0,
              top: 0,
              bottom: 0,
              left: "-130px",
              height: "100%",
              width: "auto",
              maxWidth: "none",
              objectFit: "cover",
              display: "block",
            }}
          />

          {/* content sits above the mascot */}
          <div className="relative pl-[clamp(170px,26%,260px)]" style={{ zIndex: 1 }}>
            <div className="text-xs font-bold uppercase tracking-widest opacity-60">Today</div>
            <div className="font-display font-black text-3xl tracking-tight mt-1">{doneCount} / {today_plan.length} reps done</div>
            <div className="mt-4 h-3 rounded-full bg-[#0e203b]/5 overflow-hidden">
              <div className="h-full transition-all duration-700" style={{ width: `${(doneCount/today_plan.length)*100}%`, backgroundColor: level.accent }} />
            </div>
            <div className="mt-5 flex gap-2 flex-wrap">
              <Link to="/coach" data-testid="hero-talk-coach" className="btn-pill bg-[#0e203b] text-white rounded-full px-5 h-11 text-sm font-bold flex items-center gap-2">
                Talk to your mammoth <ArrowRight size={14} />
              </Link>
              <Link to="/breathing" data-testid="hero-breathe" className="btn-pill bg-white border-2 border-[#0e203b]/10 rounded-full px-5 h-11 text-sm font-bold flex items-center gap-2">
                Take a breath
              </Link>
            </div>
          </div>
        </div>

        {/* XP card */}
        <div className="md:col-span-4 rounded-3xl p-7 shadow-lg border border-[#0e203b]/5" style={{ backgroundColor: level.surface }} data-testid="dashboard-xp">
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-widest opacity-60">Your XP</div>
            <div className="flex items-center gap-1 text-xs font-bold"><Flame size={14} /> {user.streak || 0} day streak</div>
          </div>
          <div className="font-display font-black text-5xl tracking-tighter mt-2">{user.xp}</div>
          {next_level ? (
            <>
              <div className="text-xs mt-3 opacity-70">{progress_to_next}% to Stage {next_level.level} · {next_level.name}</div>
              <div className="mt-2 h-2 rounded-full bg-[#0e203b]/5 overflow-hidden">
                <div className="h-full" style={{ width: `${progress_to_next}%`, backgroundColor: level.accent }} />
              </div>
            </>
          ) : (
            <div className="text-xs mt-3 opacity-70">Top stage reached. Keep training.</div>
          )}
        </div>

        {/* Today plan */}
        <div className="md:col-span-8 rounded-3xl p-7 shadow-lg border border-[#0e203b]/5" style={{ backgroundColor: level.surface }} data-testid="dashboard-today">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-bold text-xl">Today's training</div>
            <div className="text-xs uppercase tracking-widest opacity-60">{level.name}</div>
          </div>
          <div className="space-y-2">
            {today_plan.map(p => {
              const Icon = iconMap[p.icon] || Sparkles;
              return (
                <button
                  key={p.id}
                  data-testid={`task-${p.id}`}
                  onClick={() => nav(taskRoute[p.type] || "/dashboard")}
                  className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left ${p.done ? "bg-[#0e203b]/5 border-transparent opacity-70" : "bg-white border-[#0e203b]/5 hover:border-[#0e203b]"}`}
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: p.done ? "#0e203b" : level.accent }}>
                    {p.done ? <Check size={18} color="white" /> : <Icon size={18} strokeWidth={2.5} />}
                  </div>
                  <div className="flex-1">
                    <div className="font-bold">{p.title}</div>
                    <div className="text-xs opacity-60">+{p.xp} XP</div>
                  </div>
                  <ArrowRight size={16} />
                </button>
              );
            })}
          </div>
        </div>

        {/* Cornerstones */}
        <div className="md:col-span-4 rounded-3xl p-7 shadow-lg border border-[#0e203b]/5" style={{ backgroundColor: level.surface }} data-testid="dashboard-cornerstones">
          <div className="font-display font-bold text-xl mb-3">Cornerstones</div>
          <div className="space-y-3">
            {Object.entries(cornerstones).map(([k, v]) => (
              <div key={k}>
                <div className="flex justify-between text-sm font-semibold">
                  <span>{k}</span><span className="opacity-60">{v}</span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-[#0e203b]/5 overflow-hidden">
                  <div className="h-full" style={{ width: `${Math.min(100, v * 10)}%`, backgroundColor: level.accent }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
